/* eslint-disable replo/no-tailwind-literal-color */

import type { EditorCanvas } from "replo-utils/lib/misc/canvas";

import React from "react";

import { useCachedPageContent } from "@agent-ui/features/page-renderer/hooks/page-cache";
import {
  useSandboxNavigate,
  useSandboxOverrideRoute,
  useSandboxPreviewBaseUrl,
} from "@agent-ui/store/sandboxNavigationStore";
import twMerge from "@replo/design-system/utils/twMerge";
import useCurrentProjectId from "@v2/hooks/useCurrentProjectId";
import { CANVAS_SIZES } from "@v2/utils/canvas-size";
import useMeasure from "react-use-measure";
import { ReploError } from "schemas/errors";

import { useViewportStore } from "../store/viewportStore";
import PagePreview from "./PagePreview";

type PageRendererProps = {
  /** The page route for sandbox pages (e.g., "/landing", "/products/shoes") */
  pageRoute: string;
  className?: string;
  /**
   * If true we will always render this page interactively from the sandbox.
   * If false we will render a static version of the page from the cache.
   */
  isInteractive?: boolean;
  isScrollable?: boolean;
  previewThumbnail?: boolean;
  /**
   * Optional override for scale calculation only.
   * Locks the logical viewport used to compute scale, without changing wrapper sizing.
   */
  targetViewport?: EditorCanvas;
  /**
   * Optional overlay elements to render on top of the page content.
   * These will be positioned correctly relative to the scaled iframe content.
   */
  overlayElements?: React.ReactNode;
  /**
   * Optional callback ref to the root container element. Useful for scoping DOM queries
   * from parent components (e.g., selecting the correct iframe instance).
   */
  containerRef?: (node: HTMLDivElement | null) => void;
  /**
   * Visually hide scrollbars while still allowing scroll (template previews).
   */
  hideScrollbars?: boolean;
};

class PageRouteRequiredError extends ReploError {
  constructor(pageRoute: string) {
    super({
      message: "Page route not found",
      additionalData: { pageRoute },
    });
  }
}

function PageRenderer({
  pageRoute,
  className,
  isInteractive = false,
  isScrollable = true,
  previewThumbnail,
  targetViewport,
  overlayElements,
  containerRef,
  hideScrollbars,
}: PageRendererProps) {
  if (!pageRoute) {
    throw new PageRouteRequiredError(pageRoute);
  }

  const projectId = useCurrentProjectId();
  const sandboxNavigate = useSandboxNavigate();

  const { html: cachedPageHtml } = useCachedPageContent({
    pageRoute,
    projectId,
  });

  // NOTE (Yuxin, 2026-01-27): Use Cloudflare Sandbox preview proxy
  // URL format: http://8734-{sandboxId}.localhost:8787/{route}
  const sandboxOverrideRoute = useSandboxOverrideRoute();
  const previewBaseUrl = useSandboxPreviewBaseUrl();

  // NOTE (Yuxin, 2026-01-31): Listen for navigation events from the scaffold iframe
  // When a link is clicked in the preview, the scaffold sends a postMessage
  // which we use to update the sandbox navigation store (source of truth)
  React.useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // Check for our specific message type
      if (event.data?.type !== "replo:navigate") {
        return;
      }

      const route = event.data.route as string;
      if (!route) {
        return;
      }

      // Update the sandbox navigation store - this is the source of truth
      // for the current iframe route, and will update the page selector
      sandboxNavigate(route);
    };

    window.addEventListener("message", handleMessage);
    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, [sandboxNavigate]);

  // NOTE (Yuxin, 2026-01-28): Build preview URL from base URL set by useCurrentChat
  const desiredRoute = sandboxOverrideRoute ?? pageRoute;

  // NOTE (Yuxin, 2026-01-25): Use overrideRoute in key to force iframe re-mount on navigation
  const iframeKey = sandboxOverrideRoute || 0;

  const currentViewport = useViewportStore((state) => state.currentViewport);
  const { customWidth, viewportMode } = useViewportStore();

  const [measureRef, containerDimensions] = useMeasure();

  // NOTE (Kurt, 2025-08-13): Since we are passing in our own ref to this component
  // and we are using useMeasure, we need to merge the internal measure ref with
  // the external containerRef callback.
  // NOTE (Kurt, 2025-11-05): We ignore the exhaustive dependencies warning because we want to prevent an infinite loop
  // where useMeasure returns a new ref function on every render causing setContainerRef to be recreated every render.
  // biome-ignore lint/correctness/useExhaustiveDependencies: see docblock for explanation
  const setContainerRef = React.useCallback(
    (node: HTMLDivElement | null) => {
      measureRef(node);
      if (containerRef) {
        containerRef(node);
      }
    },
    [containerRef],
  );

  // Get viewport-specific width from CANVAS_SIZES; allow optional override for scaling only
  const effectiveViewport: EditorCanvas = targetViewport ?? currentViewport;
  const viewportWidth =
    targetViewport != null
      ? CANVAS_SIZES[effectiveViewport].defaultFrameWidth
      : customWidth ?? CANVAS_SIZES[effectiveViewport].defaultFrameWidth;

  const scale = React.useMemo(() => {
    if (previewThumbnail) {
      return 0.25;
    }

    if (viewportMode === "responsive" && customWidth == null) {
      return 1;
    }

    if (containerDimensions.width > 0) {
      const scaleToFit = containerDimensions.width / viewportWidth;
      return Math.min(scaleToFit, 1);
    }

    return 1;
  }, [
    containerDimensions.width,
    viewportWidth,
    previewThumbnail,
    viewportMode,
    customWidth,
  ]);

  const iframeContent = (() => {
    if (!desiredRoute) {
      return null;
    }
    if (!previewBaseUrl) {
      return (
        <div className="h-full w-full flex items-center justify-center bg-slate-100">
          <div className="text-center p-8 max-w-md">
            <div className="text-4xl mb-4">🚧</div>
            <h3 className="text-lg font-semibold text-slate-700 mb-2">
              Preview Loading
            </h3>
            <p className="text-sm text-slate-500 mb-4">
              Live preview is loading and should be available soon...
            </p>
          </div>
        </div>
      );
    }
    // Remove leading slash if present since base URL already ends without one
    const normalizedRoute = desiredRoute.startsWith("/")
      ? desiredRoute.slice(1)
      : desiredRoute;
    const baseUrl = previewBaseUrl.endsWith("/")
      ? previewBaseUrl.slice(0, -1)
      : previewBaseUrl;
    const url = `${baseUrl}/${normalizedRoute}`;
    return (
      <iframe
        src={url}
        key={iframeKey}
        className="h-full w-full border-0"
        sandbox="allow-scripts allow-same-origin"
      />
    );
  })();

  return (
    <div
      ref={setContainerRef}
      className={twMerge(
        "flex h-full w-full flex-col relative overflow-hidden",
        className,
      )}
    >
      <div
        className={twMerge("relative h-full w-full overflow-hidden", className)}
      >
        {/* NOTE (Yuxin, 2026-01-25): Removed error state since we're using simple localhost:4000 */}

        <div
          style={{
            transformOrigin: "top left",
            transform: `scale(${scale})`,
            width: targetViewport ? `${viewportWidth}px` : `${100 / scale}%`,
            height:
              targetViewport && containerDimensions.height > 0
                ? `${containerDimensions.height / scale}px`
                : `${100 / scale}%`,
            overflow: "hidden",
          }}
          className="flex h-full flex-col"
        >
          <div className="flex h-full w-full flex-col">
            <div className="relative flex h-full w-full flex-col items-center justify-start">
              <PagePreview
                cachedPageHtml={cachedPageHtml}
                isLoadingPage={false}
                isInteractive={isInteractive}
                isScrollable={isScrollable}
                hideScrollbars={hideScrollbars}
              />
              {iframeContent}
            </div>
          </div>
          {/* Overlay Elements - positioned relative to scaled content */}
          {overlayElements && (
            <div className="absolute inset-0 pointer-events-none z-[99999]">
              {overlayElements}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default PageRenderer;
